import { useEffect, useState } from "react";
import { message } from "antd";
import {
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
} from "@ant-design/icons";
 import { isTokenExpired } from "@/utils/jwt";
import Login from "@/pages/Login";
import blueflashLogo from "./assets/logo/blueflashlogo.png";
import spotflashLogo from "./assets/logo/spotflashlogo.png";
import docflashLogo from "./assets/logo/docflashlogo.png";
import logo from "./assets/logo/logo.png";
import edkaslogo from "./assets/logo/edkaslogo.png";
import userServices from "./services/auth.service";
import useAuthStore from "./store/useAuthStore";
import AppSwitcher from "./components/AppSwitcher";

const APPS = [
  {
    key: "blueflash",
    appId: import.meta.env.VITE_BLUEFLASH_APP_ID,
    label: "BlueFlash",
    path: "/blueflash/",
    color: "#2563eb",
    description: "Planned maintenance, equipment and jobs.",
    logo: blueflashLogo,
  },
  {
    key: "spotflash",
    appId: import.meta.env.VITE_SPOTFLASH_APP_ID,
    label: "SpotFlash",
    path: "/spotflash/",
    color: "#7c3aed",
    description: "Vessel inspections, findings and checklists.",
    logo: spotflashLogo,
  },
  {
    key: "docflash",
    appId: import.meta.env.VITE_DOCFLASH_APP_ID,
    label: "DocFlash",
    path: "/docflash/",
    color: "#db2777",
    description: "Extract and manage information from documents.",
    logo: docflashLogo,
  },
];

const SESSION_CHECK_MS = 60 * 1000;

const headerButton =
  "flex h-8 w-8 cursor-pointer items-center justify-center rounded-[7px] border-0 bg-transparent p-0 text-[15px] text-gray-500 transition-colors duration-150 hover:bg-zinc-100 hover:text-[#14161b]";

// Auth gate: the launcher is only ever rendered for a valid session.
export default function App() {
  const authenticated = useAuthStore((s) => s.authenticated);
  const accessToken = useAuthStore((s) => s.accessToken);
  const logout = useAuthStore((s) => s.logout);

  const hasSession = authenticated && !isTokenExpired(accessToken);

  // Drop a stale persisted session and keep checking while the shell is open.
  useEffect(() => {
    if (!authenticated) return;

    const check = () => {
      if (isTokenExpired(useAuthStore.getState().accessToken)) {
        logout();
        message.warning("Your session expired. Please sign in again.");
      }
    };

    check();
    const id = setInterval(check, SESSION_CHECK_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", check);
    };
  }, [authenticated, logout]);

  if (!hasSession) return <Login />;
  return <Launcher />;
}

// Local branding keyed by app name (lowercase). API has no color/logo image.
const APP_META = {
  blueflash: { color: "#2563eb", logo: blueflashLogo },
  spotflash: { color: "#7c3aed", logo: spotflashLogo },
  docflash: { color: "#db2777", logo: docflashLogo },
};
const FALLBACK_COLORS = ["#0891b2", "#ea580c", "#16a34a", "#9333ea"];

// Flatten the API tree into launchable cards.
// The parent ("BlueFlash Platform") is this shell itself, so only sub_apps are
// shown; a parent with no sub_apps is shown as an app on its own.
// Flat list -> launchable cards.
// - Apps that have children (e.g. "BlueFlash Platform") are this shell itself, so they're hidden.
// - Child apps (SpotFlash, Blueflash) are shown.
// - Standalone top-level apps with no children (e.g. "Flash") are shown on their own.
 
const toLaunchable = (list = []) => {
  const active = list.filter((a) => a.is_active !== false);

  const appName = String(import.meta.env.VITE_APP_NAME || "")
    .trim()
    .toLowerCase();

  if (!appName) {
    console.warn("VITE_APP_NAME is not configured");
    return [];
  }

  // Find the current platform / parent app by name.
  const currentPlatform = active.find(
    (app) => String(app.name || "").trim().toLowerCase() === appName
  );

  if (!currentPlatform) {
    console.warn(
      `Platform "${import.meta.env.VITE_APP_NAME}" was not found in applications`
    );
    return [];
  }

  // If this is a standalone application, show the application itself.
  if (!currentPlatform.app_id) {
    return [];
  }

  // Find only direct children of the current platform.
  const children = active
    .filter(
      (app) =>
        String(app.parent_app_id) === String(currentPlatform.app_id)
    )
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));

  // If the platform has children, launch those children.
  if (children.length > 0) {
  return children.map((app, i) => {
  const meta = APP_META[String(app.name || "").trim().toLowerCase()] || {};

  return {
    key: app.app_id,
    appId: app.app_id,
    label: app.name,
    path: app.app_url,
    description: app.description || "",
    color:
      meta.color ||
      FALLBACK_COLORS[i % FALLBACK_COLORS.length],
    logo: meta.logo || null,
  };
});
  }

  // Standalone application: show the platform itself.
  const meta =
    APP_META[String(currentPlatform.name || "").toLowerCase()] || {};

  return [
    {
      key: currentPlatform.app_id,
      appId: currentPlatform.app_id,
      label: currentPlatform.name,
      path: currentPlatform.app_url,
      description: currentPlatform.description || "",
      color: meta.color || FALLBACK_COLORS[0],
      logo: currentPlatform.logo_url || meta.logo || null,
    },
  ];
};


function Launcher() {
  const username = useAuthStore((s) => s.username);
  const logout = useAuthStore((s) => s.logout);
  const [openingKey, setOpeningKey] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

const openApp = async (e, app) => {
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  if (openingKey) return;

  if (!app.appId) {
    message.warning("This application does not have an id configured");
    return;
  }

  setOpeningKey(app.key);
  try {
    console.log("1. requesting auto-login link for", app.appId);
    const res = await userServices.generateAutoLoginLink(app.appId);
    console.log("2. raw response", res);

    const body = res?.data ?? res;
    const payload = body?.data?.auto_login_token ? body.data : body;
    const { auto_login_token, app_url } = payload;
    console.log("3. token/app_url", auto_login_token, app_url);

    if (!auto_login_token) throw new Error("No auto_login_token in response");

    const url = new URL(app_url || app.path, window.location.origin);
    if (!url.pathname.includes("/auto-login")) {
      url.pathname = `${url.pathname.replace(/\/?$/, "/")}auto-login`;
    }
    url.searchParams.set("auto_login_token", auto_login_token);
    console.log("4. redirecting to", url.toString());

    window.location.assign(url.toString());
  } catch (err) {
    console.error("openApp failed", err);
    if (err.response?.status === 401) {
      logout();
      message.warning("Your session expired. Please sign in again.");
      return;
    }
    message.error(err.response?.data?.message || err.message || "Failed to open application.");
    setOpeningKey(null);
  }
};


    const loadApps = async () => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await userServices.getApplications();
      console.log("datadata",data)
      setApps(toLaunchable(data));
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
        message.warning("Your session expired. Please sign in again.");
        return;
      }
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadApps(); }, []);

  return (
    <div className="flex min-h-screen flex-col bg-[#fafafa]">
      {/* Header */}
      <header className="h-14 shrink-0 border-b border-zinc-200 bg-white">
        <div className="mx-auto flex h-full w-full max-w-[960px] items-center justify-between px-6 max-[520px]:px-4">
          <div className="flex items-center gap-2.5">
            <img
              src={edkaslogo}
              alt="BlueFlash"
              className="h-[70px] w-[70px] rounded-md object-contain"
            />
            <span className="text-sm font-semibold text-[#14161b] max-[520px]:text-[13px]">
              BlueFlash Platform
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              className={headerButton}
              title="Settings"
              aria-label="Settings"
            >
              <SettingOutlined />
            </button>

            {username && (
              <span className="hidden max-w-[160px] truncate text-[13px] text-zinc-500 sm:block">
                {username}
              </span>
            )}

            <div
              className="flex h-8 w-8 items-center justify-center rounded-full bg-zinc-100 text-sm text-gray-500"
              title={username || "User"}
            >
              <UserOutlined />
            </div>

            <button
              type="button"
              className={headerButton}
              title="Sign out"
              aria-label="Sign out"
              onClick={logout}
            >
              <LogoutOutlined />
            </button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="mx-auto w-full max-w-[960px] flex-1 px-6 pt-9 pb-12 max-[760px]:pt-8 max-[520px]:px-4 max-[520px]:pt-7 max-[520px]:pb-9 max-[360px]:px-3">
        {/* Hero */}
        <section className="flex flex-col items-center px-3 pb-[42px] text-center max-[520px]:pb-8">
          <img
            src={logo}
            alt="BlueFlash"
            className="mb-[18px] h-[125px] w-[125px] rounded-[20px] object-contain max-[520px]:mb-4 max-[520px]:h-[105px] max-[520px]:w-[105px]"
          />

          <h1 className="text-[22px] leading-[1.3] font-semibold text-[#14161b] max-[520px]:text-xl">
            BlueFlash Platform
          </h1>

          <p className="mt-2 text-sm leading-normal text-gray-500 max-[520px]:text-[13px]">
            Choose an application to get started
          </p>
        </section>

        {/* Applications */}
        <section className="w-full">
          <h2 className="mb-3.5 text-base leading-[1.4] font-semibold text-[#14161b]">
            Applications
          </h2>

          {loading ? (
            <div className="py-16 text-center text-sm text-zinc-400">
              Loading applications…
            </div>
          ) : error ? (
            <div className="py-16 text-center text-sm text-zinc-500">
              Couldn't load applications.{" "}
              <button
                onClick={loadApps}
                className="cursor-pointer border-0 bg-transparent font-semibold text-blue-600"
              >
                Retry
              </button>
            </div>
          ) : apps.length === 0 ? (
            <div className="py-16 text-center text-sm text-zinc-400">
              No applications available
            </div>
          ) : (
            <div className="grid w-full grid-cols-3 gap-4 max-[760px]:grid-cols-2 max-[520px]:grid-cols-1 max-[520px]:gap-3">
              {apps.map((app) => (
                <a
                  key={app.key}
                  href={app.path}
                  onClick={(ev) => openApp(ev, app)}
                  aria-busy={openingKey === app.key}
                  className={`${openingKey ? "pointer-events-none opacity-60 " : ""}group relative flex flex-col rounded-xl border border-zinc-200 bg-white p-5 text-center text-inherit no-underline shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-[transform,border-color,box-shadow] duration-[180ms] hover:-translate-y-[3px] hover:border-zinc-300 hover:shadow-[0_8px_20px_rgba(0,0,0,0.07)] max-[520px]:min-h-[190px] max-[360px]:p-[18px]`}
                >
                  <div className="mx-auto flex h-[72px] w-[72px] items-center justify-center overflow-hidden rounded-[15px] bg-[linear-gradient(135deg,#F1EDD8,#88913c)]">
                    {app.logo ? (
                      <img
                        src={app.logo}
                        alt={`${app.label} logo`}
                        className="block h-full w-full object-contain"
                      />
                    ) : (
                      <span className="text-xl font-bold text-white">
                        {app.label.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>

                  <div className="mt-5">
                    <h3 className="text-[17px] leading-[1.4] font-semibold text-[#14161b]">
                      {app.label}
                    </h3>
                    <p className="mt-[7px] text-[13px] leading-[1.6] text-gray-500">
                      {app.description}
                    </p>
                  </div>

                  <div
                    className="mt-auto flex -translate-x-1 items-center gap-1.5 pt-5 text-[13px] font-semibold opacity-0 transition-[opacity,transform] duration-[180ms] group-hover:translate-x-0 group-hover:opacity-100 max-[520px]:translate-x-0 max-[520px]:opacity-100"
                    style={{ color: app.color }}
                  >
                    <span>{openingKey === app.key ? "Opening…" : "Open"}</span>
                    <span className="text-base leading-none">→</span>
                  </div>
                </a>
              ))}
            </div>
          )}
        </section>
      </main>
 <AppSwitcher />
      {/* Footer */}
      <footer className="shrink-0 border-t border-zinc-200 bg-white px-6 py-4 text-center text-xs text-zinc-400">
        © 2026 BlueFlash Platform
      </footer>
    </div>
  );
}
